import { ChangeDetectorRef, Directive, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, map, Observable, switchMap, tap } from 'rxjs';
import { ItemModifier, ItemModifierGroup, MenuItemDto, MenuItemSizeDto, MenuPrice } from 'src/app/api/v1/models';
import { ModifierItem } from 'src/app/components/modifier-list/modifier-item.interface';
import { SeoService } from 'src/app/services/seo.service';
import { ModalSwipeDirective } from 'src/app/shared/directives/modal-swipe/modal-swipe.directive';
import { UIMenuItemDto } from 'src/app/shared/interfaces/ui-menu-item-dto.interface';
import { ConfigRepository } from 'src/app/shared/repositories/config.repository';
import { MenuListRepository } from 'src/app/shared/repositories/menu-list.repository';
import { BasketRepository } from 'src/app/shared/repositories/basket.repository';
import { BreakpointService } from 'src/app/shared/services/breakpoint.service';
import { SessionRepository } from 'src/app/shared/repositories/session.repository';

/**
 * Описывает форму
 */
interface IForm {
  count: FormControl<number>;
  item: FormControl<UIMenuItemDto | null>;
  modifiers: FormArray<FormControl<ModifierItem[] | null>>;
}

@Directive()
export abstract class ProductInfoPageAbstract implements OnInit {

  @ViewChild(ModalSwipeDirective) modalSwipe: ModalSwipeDirective;

  private storeId: number = this.configStore.activeOutletId;

  form: FormGroup<IForm> = new FormGroup<IForm>({
    item: new FormControl(null),
    count: new FormControl<number>(1, { nonNullable: true }),
    modifiers: new FormArray<FormControl<ModifierItem[] | null>>([]),
  });
  modifiersArray = new FormArray<FormControl<ModifierItem[] | null>>([]);
  currentSize!: MenuItemSizeDto;
  firstSize!: MenuItemSizeDto;
  totalPrice: number = 0;

  data$: Observable<MenuItemDto | undefined>;
  isBasketMode$: Observable<boolean> = this.configStore.isBasketMode$;

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected menuListRepository: MenuListRepository,
    protected basketStore: BasketRepository,
    protected configStore: ConfigRepository,
    public breakPoint: BreakpointService,
    protected seoService: SeoService,
    protected cdr: ChangeDetectorRef,
    protected sessionRepository: SessionRepository,
  ) { }

  protected getProductData(): Observable<UIMenuItemDto> {
    return this.route.params.pipe(
      map(params => params['slug']),
      filter((slug): slug is string => !!slug),
      map(slug => ({slug, categoryId: null})),
      switchMap(productParams => this.menuListRepository.getItemBySlugFromCategory(productParams.slug, productParams.categoryId)),
    )
  }

  ngOnInit(): void {
    this.data$ = this.getProductData().pipe(
      tap(data => {
        if (!data) { return; }
        this.form.controls.item.setValue(data)

        const defaultSize = data.itemSizes.find(size => size.isDefault);

        this.currentSize = defaultSize ?? data.itemSizes[0];
        this.firstSize = defaultSize ?? data.itemSizes[0];

        this.prepareFormArray(this.currentSize.itemModifierGroups);

        this.updateTotalPrice();

        this.seoService.setDynamicDataMetaTags(data.name, data.description);

        this.cdr.markForCheck();
      })
    )
  }

  private prepareFormArray(groups: ItemModifierGroup[]): void {
    this.modifiersArray.clear();

    groups.forEach(group => {
      const control = new FormControl<ModifierItem[] | null>(null);

      if (group.restrictions.maxQuantity === 1) {
        control.setValue(this.getGroupData(group.items, group.itemGroupId));
      }
      else {
        control.setValue(this.getSingleData(group.items, group.itemGroupId))
      }

      this.modifiersArray.push(control);
    })

    this.form.setControl('modifiers', this.modifiersArray);
  }

  private getPriceByStoreId(prices: MenuPrice[], outletId: number): number {
    return prices.find(price => price.storeId === outletId)?.price || 0
  }

  private getSingleData(items: ItemModifier[], groupId: string): ModifierItem[] | null {
    const data: ModifierItem[] = [];

    items.forEach(item => {
      data.push({
        name: item.name,
        quantity: item.restrictions.byDefault,
        sku: item.sku,
        prices: item.prices,
        itemGroupId: groupId,
        freeQuantity: 0,
      })
    })

    return data.length ? data : null;
  }

  private getGroupData(items: ItemModifier[], groupId: string): ModifierItem[] | null {
    let data: ModifierItem | null = {
      name: '',
      quantity: 0,
      sku: '',
      prices: null,
      itemGroupId: groupId,
      freeQuantity: 0,
    };

    for (let i = 0; i < items.length; i++) {
      if (items[i].restrictions.byDefault) {
        data = {
          name: items[i].name,
          quantity: items[i].restrictions.byDefault,
          sku: items[i].sku,
          prices: items[i].prices,
          itemGroupId: groupId,
          freeQuantity: items[i].restrictions.freeQuantity,
        }
        break;
      }
    }

    return [data];
  }

  protected getModifiersPrice(): number {
    const modifiers = this.form.controls.modifiers.getRawValue().flat().filter((item): item is ModifierItem => !!item);
    if(!modifiers){return 0}
    return this.sessionRepository.getModifiersPrice(modifiers, this.storeId);
  }

  private updateTotalPrice(): void {
    const productPrice = this.getPriceByStoreId(this.currentSize.prices, this.storeId);
    this.totalPrice = (productPrice + this.getModifiersPrice()) * this.form.controls.count.value;
  }

  private setFreeQuantity(modifierGroup: ItemModifierGroup) {
    this.form.controls.modifiers.getRawValue().forEach((items, g) => {
      if (!items?.length) { return; }

      const groupFreeQuantity = modifierGroup.restrictions.freeQuantity;
      let tmpCurrentFreeQnt = 0;

      const itemsWithMaxPrice = items
        .filter(value => value.quantity > 0)
        .map(value => ({ ...value, price: this.getPriceByStoreId(value.prices || [], this.storeId) }))
        .filter(value => !!value.price)
        .sort((a, b) => b.price! - a.price! || (a.name > b.name ? 1 : -1))
        .filter(value => {
          const item = modifierGroup.items.find(i => i.sku === value.sku);
          if (!item || groupFreeQuantity <= tmpCurrentFreeQnt) { return false; }

          const minFreeQnt = modifierGroup.childModifiersHaveMinMaxRestrictions
            ? Math.min(value.quantity, item.restrictions.freeQuantity, groupFreeQuantity - tmpCurrentFreeQnt)
            : Math.min(value.quantity, groupFreeQuantity - tmpCurrentFreeQnt);

          tmpCurrentFreeQnt += minFreeQnt;
          value.freeQuantity = minFreeQnt;
          return true;
        });

      items.forEach(item => {
        item.freeQuantity = itemsWithMaxPrice.find(i => i.sku === item.sku)?.freeQuantity || 0;
      });
      this.form.controls.modifiers.controls[g].patchValue(items);
    });
  }

  onChangeModifiers(modifierGroup: ItemModifierGroup): void {
    this.setFreeQuantity(modifierGroup);
    this.updateTotalPrice();
    this.cdr.markForCheck();
  }

  onChangeCount(): void {
    this.updateTotalPrice();
    this.cdr.markForCheck();
  }

  onChangeSize(size: MenuItemSizeDto): void {
    if (size === this.currentSize) { return }
    this.currentSize = size;
    this.prepareFormArray(this.currentSize.itemModifierGroups);

    this.updateImage();
    this.updateTotalPrice();
    this.cdr.markForCheck();
  }

  updateImage(): void {
    if (this.firstSize.buttonImage['src'] && !this.currentSize.buttonImage['src']) {
      this.currentSize.buttonImage = this.firstSize.buttonImage;
    }
  }

  onAddToBasket(): void {
    if (!this.form.valid || !this.form.controls.item.value) { return }

    const modifierGroups: ModifierItem[] = this.form.controls.modifiers.getRawValue().flat()
      .filter(item => item?.quantity)
      .map(item => item!);

    const count = this.form.controls.count.value;

    this.basketStore.addItem({
      item: this.form.controls.item.value,
      size: this.currentSize,
      count: count,
      modifiers: modifierGroups,

      appliedDiscount: 0,
      itemKey: '',
      totalPrice: this.sessionRepository.getItemTotal(this.currentSize.prices, modifierGroups, count)
    })

    this.close();
  }

  close() {
    if (this.breakPoint.isMobile) {
      this.closeMobileModal();
    } else {
      this.closeModal();
    }
  }

  closeMobileModal(): void {
    this.modalSwipe.hideModal();
  }

  closeModal(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  setAllergenFilter(allergen: string) {
    this.sessionRepository.toggleShowFilterBar(true);
    this.sessionRepository.toggleActiveAllergenFilter(allergen);
    this.close();
  }

  setLabelFilter(label: string) {
    this.sessionRepository.toggleShowFilterBar(true);
    this.sessionRepository.toggleActiveLabelFilter(label);
    this.close();
  }
}
